<script setup lang="ts">
import { Pages } from "@/pages/pages";
import type { RoleGroup } from "./RoleGroups";
import { useMainStore } from "@/stores/useMainStore";
import { watchEffect } from "vue";

export interface Props {
  requiredRoleGroups?: RoleGroup[];
  redirect?: string;
}

const main = useMainStore();

const props = withDefaults(defineProps<Props>(), {
  redirect: Pages.Home,
});

const emits = defineEmits<{
  (e: "accessChanged", access: boolean): void;
}>();

const validateRoleGroupAsync = async function (
  roleGroup: RoleGroup,
  userRoles: string[]
) {
  const requireRolesValidaiton = roleGroup.roles?.length > 0;

  // If the user has any of the required roles or
  // if no roles are required, then the user has access
  const hasRole =
    !requireRolesValidaiton ||
    userRoles.some((userRole) =>
      roleGroup.roles.some((role) => role === userRole)
    );

  // If the user doesn't have access or no roles are required,
  // then validate additional restrictions
  if (
    (!hasRole || !requireRolesValidaiton) &&
    roleGroup.validateRestrictionsAsync
  )
    return await roleGroup.validateRestrictionsAsync();
  return hasRole;
};

const hasAccessAsync = async function () {
  if (!main.roles) return;

  const userRoles = main.roles;

  let hasAccess = true;

  try {
    // All groups should be successfully validated
    // in order for the user to have access
    for (const roleGroup of props.requiredRoleGroups ?? []) {
      if (!(await validateRoleGroupAsync(roleGroup, userRoles))) {
        hasAccess = false; // debugging for now and it's returning false for some reason
        break;
      }
    }
  } catch (e) {
    console.error("AccessHandler.vue: " + e);
    hasAccess = false;
  }

  console.debug(
    "AccessHandler.vue: hasAccess = " +
      hasAccess +
      ", userRoles = " +
      JSON.stringify(userRoles) +
      ", props.requiredRoleGroups = " +
      JSON.stringify(props.requiredRoleGroups)
  );
  emits("accessChanged", hasAccess);

  if (props.redirect && !hasAccess) window.location.href = props.redirect;
};

hasAccessAsync();

watchEffect(() => {
  hasAccessAsync();
});
</script>
